import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // Skip the auth probe on public routes so guest visits don't spam 401s.
    if (typeof window !== "undefined" && (window.location.pathname.startsWith("/g/") || window.location.pathname === "/reset-password")) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      logger.debug("auth/me failed (treating as logged out):", err?.response?.status);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // The httpOnly access_token + refresh_token cookies set by the backend are
    // the ONLY place tokens live in the browser. We intentionally don't touch
    // localStorage — a compromised script cannot exfiltrate the JWT.
    //
    // iter184 — Server now returns permissions_effective + is_owner on
    // /auth/login. We still fire /auth/me right after to guarantee any
    // future field additions are picked up without a second refresh.
    setUser(data);
    try {
      const { data: me } = await api.get("/auth/me");
      setUser(me);
    } catch {
      /* login-response payload is a valid fallback */
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.debug("logout call failed:", err?.message);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh: fetchMe }),
    [user, loading, login, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
